import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  paidMediaOverviewService,
  type PaidMediaOverviewAttributionMode,
  type PaidMediaOverviewCampaignDetailData,
  type PaidMediaOverviewGranularity,
  type PaidMediaOverviewPlatform,
} from "src/services/PaidMediaOverviewAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  composeMockCampaignDetail,
  convertCampaignDetailMoney,
  isMockOnlyPlatform,
  OVERVIEW_CURRENCY,
} from "../utils/paidMediaOverviewComposition";
import { useAsyncResource } from "./useAsyncResource";

const CAMPAIGN_NOT_FOUND_CODE = "CAMPAIGN_NOT_FOUND";
const HTTP_NOT_FOUND = 404;

export interface UsePaidMediaOverviewCampaignDetailParams {
  platform: PaidMediaOverviewPlatform | null;
  campaignId: string | null;
  startDate: string;
  endDate: string;
  granularity: PaidMediaOverviewGranularity;
  attributionMode: PaidMediaOverviewAttributionMode;
}

export interface PaidMediaOverviewCampaignDetailBundle {
  detail: PaidMediaOverviewCampaignDetailData | null;
  currency: string | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
}

export function usePaidMediaOverviewCampaignDetail({
  platform,
  campaignId,
  startDate,
  endDate,
  granularity,
  attributionMode,
}: UsePaidMediaOverviewCampaignDetailParams): PaidMediaOverviewCampaignDetailBundle {
  const { t, i18n } = useTranslation();
  const fallbackError = t("paidMediaOverview.errors.requestFailed");
  // TikTok and Snapchat campaigns have no endpoint to call; they are served
  // from the same mock dataset the rest of the dashboard composes.
  const isMockCampaign = isMockOnlyPlatform(platform);
  const enabled = Boolean(platform && campaignId && startDate && endDate) && !isMockCampaign;
  // The drawer renders the API's `insight` copy, so it has to follow the language too.
  const lang = getApiLangFromI18n(i18n);

  const request = useCallback(() => {
    if (!platform || !campaignId) {
      return Promise.reject(new Error(fallbackError));
    }

    return paidMediaOverviewService.fetchCampaignDetail({
      platform,
      campaign_id: campaignId,
      start_date: startDate,
      end_date: endDate,
      granularity,
      attribution_mode: attributionMode,
      lang,
    });
  }, [platform, campaignId, startDate, endDate, granularity, attributionMode, lang, fallbackError]);

  const resource = useAsyncResource(enabled ? request : null, fallbackError);

  const mockDetail = useMemo(() => {
    if (!isMockCampaign || !platform || !campaignId) {
      return null;
    }
    return composeMockCampaignDetail(platform, campaignId, {
      startDate,
      endDate,
      granularity,
      platformFilter: platform,
      attributionMode,
      campaignId,
      objectiveOrNetwork: "",
      status: "",
    });
  }, [isMockCampaign, platform, campaignId, startDate, endDate, granularity, attributionMode]);

  if (isMockCampaign) {
    return {
      detail: mockDetail,
      currency: OVERVIEW_CURRENCY,
      loading: false,
      error: null,
      notFound: Boolean(platform && campaignId) && !mockDetail,
    };
  }

  const notFound =
    resource.errorCode === CAMPAIGN_NOT_FOUND_CODE || resource.errorStatus === HTTP_NOT_FOUND;

  return {
    detail: convertCampaignDetailMoney(resource.data?.data ?? null),
    currency: OVERVIEW_CURRENCY,
    loading: resource.loading,
    error: notFound ? null : resource.error,
    notFound,
  };
}
